import * as React from 'react';
import { Program } from '~source/core/models/tv-guide';
import { dateToTimeNotation } from '~source/utils/beautify-timing';
import { calculateTimelineItemLayout } from '~source/utils/timeline-layout';
import {
    isProgramNowOnTv,
    registerCallback,
    unregisterCallback,
    startUpdater,
} from '~source/utils/program-status-updater';
import $ from './timeline-item.scss';
import cx from '~source/utils/join-class-names';
import { Icon } from '~source/ui/components/atoms/icon/icon';
import { ProgramModal } from '~source/ui/components/molecules/program-modal/program-modal';

export interface TimelineItemProps {
    program: Program;
    isCurrent?: boolean;
    channelName: string;
}

export const TimelineItem: React.FC<TimelineItemProps> = ({
    program,
    isCurrent = false,
    channelName,
}) => {
    const startDateRef = React.useRef<Date>(new Date(program.dateStart));
    const endDateRef = React.useRef<Date>(new Date(program.dateEnd));
    const [currentStatus, setCurrentStatus] = React.useState(isCurrent);

    // Update refs when program changes
    React.useEffect(() => {
        startDateRef.current = new Date(program.dateStart);
        endDateRef.current = new Date(program.dateEnd);
    }, [program.dateStart, program.dateEnd]);

    const updateStatus = () => {
        const startDate = startDateRef.current;
        const endDate = endDateRef.current;
        const isCurrentlyOnTv = isProgramNowOnTv(startDate, endDate);
        setCurrentStatus(isCurrentlyOnTv);
    };

    // Register callback for real-time updates
    React.useEffect(() => {
        updateStatus();
        registerCallback(updateStatus);
        startUpdater();

        return () => {
            unregisterCallback(updateStatus);
        };
    }, []);

    const startDate = startDateRef.current;
    const endDate = endDateRef.current;
    const now = new Date();
    const isPassed = !currentStatus && endDate < now;

    const { left, width } = calculateTimelineItemLayout(startDate, endDate);

    const [showModal, setShowModal] = React.useState(false);

    const onClickItem = (e: React.MouseEvent<HTMLDivElement>) => {
        // Prevent navigation if dragging occurred
        // Check the data attribute on the timeline wrapper (set synchronously on drag end)
        const timelineWrapper = e.currentTarget.closest(
            '[role="region"][aria-label="Tijdlijn"]',
        ) as HTMLElement;
        const wasDragging = timelineWrapper?.getAttribute('data-was-dragging') === 'true';

        if (wasDragging) {
            e.preventDefault();
            e.stopPropagation();
            if (timelineWrapper) {
                timelineWrapper.removeAttribute('data-was-dragging');
            }
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
    };

    const onKeyDownItem: React.KeyboardEventHandler<HTMLDivElement> = React.useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowModal(true);
        }
    }, []);

    return (
        <>
            <div
                className={cx($.wrap, currentStatus && $.current)}
                style={
                    {
                        width: `${width}px`,
                        left: `${left}px`,
                        '--item-left': `${left}px`,
                        '--item-width': `${width}px`,
                    } as React.CSSProperties
                }
                onClick={onClickItem}
                role='button'
                tabIndex={0}
                onKeyDown={onKeyDownItem}
            >
                <div className={cx($.row, $.sticky)}>
                    <span className={$.date} style={{ maxWidth: `${width - 14}px` }}>
                        {isPassed && !currentStatus && (
                            <Icon
                                type='replay'
                                size={14}
                                color='orange-500'
                                className={$.replayIcon}
                            />
                        )}
                        {dateToTimeNotation(startDate)} - {dateToTimeNotation(endDate)}
                    </span>
                </div>
                <div className={cx($.row, $.sticky)}>
                    <h4 className={$.title} style={{ maxWidth: `${width - 14}px` }}>
                        {program.live && <span className={$.live}>LIVE</span>}
                        {program.title}
                    </h4>
                </div>
            </div>

            {showModal && (
                <ProgramModal
                    program={program}
                    channelName={channelName}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    );
};
